import { Radio, Card, Alert, Space, Typography, Select, Divider } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  collectionAndDeliveryDelay,
  getKpiReports,
  metricDefinitions,
  getReports,
} from '../../store/requests/dashboard';
import { useSelector } from 'react-redux';
import PerformanceCard from './PerformanceCard';
import RegionalPerformanceCard from './RegionalPerformanceCard';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Loading } from '../index';
import StoreAndCourierOperationCard from './StoreAndCourierOperationCard';
import { DASHBOARD_REPORT_PATHS } from '../../helpers/endpoints';
import {
  getRegionsWithoutStore,
  getStoresByRegion,
} from '../../store/requests/definitions';

const Dashboard = () => {
  const { t } = useTranslation();
  const [loadingCount, setLoadingCount] = useState(1);
  const [selectedDate, setSelectedDate] = useState('daily');
  const activeStore =
    useSelector((state) => state.auth?.user?.activeStore) ?? null;
  const user = useSelector((state) => state.auth.user);
  const isRegionalDirectore = user?.roles[0]?.role === 'REGIONAL_DIRECTOR';
  const isRegionalCounty = user?.roles?.some(
    (role) =>
      role.role === 'REGIONAL_MANAGER' || role.role === 'REGIONAL_EXECUTIVE',
  );
  const [performanceData, setPerformanceData] = useState([]);
  const [regionalPerformanceData, setRegionalPerformanceData] = useState([]);
  const [selectedStore, setSelectedStore] = useState([]);
  const [collectionAndDeliveryDelayData, setCollectionAndDeliveryDelayData] =
    useState([]);
  const [allPaths, setAllPaths] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [reportTypeLoading, setReportTypeLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionOptions, setRegionOptions] = useState([]);
  const [regionPerformanceData, setRegionPerformanceData] = useState([]);
  const [selectedDirectorateId, setSelectedDirectorateId] = useState(null);
  const [directorateOptions, setDirectorateOptions] = useState([]);
  const [
    directorateRegionPerformanceData,
    setDirectorateRegionPerformanceData,
  ] = useState(null);
  const [directorateStoreOptions, setDirectorateStoreOptions] = useState([]);
  const [selectedDirectorateStore, setSelectedDirectorateStore] =
    useState(null);
  const [regionStoreOptions, setRegionStoreOptions] = useState([]);
  const [selectedRegionStore, setSelectedRegionStore] = useState(null);
  const [selectedStorePerformanceData, setSelectedStorePerformanceData] =
    useState(null);

  useEffect(() => {
    if (isRegionalDirectore && user?.directorateId) {
      getRegionsWithoutStore(user.directorateId)
        .then((response) => {
          const regions = (
            response?._embedded?.regionDirectorateDtoList || []
          ).map((r) => ({
            value: r.id,
            label: `${r.code} - ${r.name}`,
          }));
          setDirectorateOptions(regions);
        })
        .catch(() => setDirectorateOptions([]));
    }
  }, [isRegionalDirectore, user]);

  useEffect(() => {
    if (isRegionalCounty && selectedRegion) {
      fetchStoresByRegionId(
        selectedRegion,
        setRegionStoreOptions,
        setSelectedRegionStore,
      );
    }
  }, [isRegionalCounty, selectedRegion]);

  useEffect(() => {
    (async () => {
      try {
        const requests = [];
        const storeId = activeStore?.id || null;
        const { data } = await metricDefinitions({ storeId }, 'BACKOFFICE');
        const allPaths = extractPaths(data.metrics);
        setAllPaths(allPaths);

        if (activeStore?.id) {
          setSelectedStore([activeStore.id]);
          setMetrics(data.metrics);
          if (!isRegionalDirectore && !isRegionalCounty) {
            requests.push(
              requestAllPaths([activeStore.id], allPaths, selectedDate),
            );
          }
        }

        if (isRegionalDirectore) {
          setMetrics(data.metrics);
          requests.push(requestRegionalPaths(allPaths, selectedDate));
        }

        if (isRegionalCounty) {
          setMetrics(data.metrics);
          requests.push(requestRegionPaths(selectedDate));
        }

        if (requests.length > 0) {
          await Promise.all(requests);
        }
      } finally {
        setLoadingCount((prev) => prev - 1);
      }
    })();
  }, [activeStore, isRegionalDirectore, isRegionalCounty]);

  function extractPaths(data) {
    let paths = new Set();
    function traverse(node) {
      paths.add(node.path);
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => traverse(child));
      }
    }
    data.forEach((node) => traverse(node));
    return Array.from(paths);
  }

  async function requestAllPaths(storeIds, paths, selectedDate = 'daily') {
    if (storeIds.length && paths.length) {
      setPerformanceData([]);
      setCollectionAndDeliveryDelayData([]);
      let responses = [];
      const payload = { storeIds };

      try {
        setLoadingCount((prev) => prev + 1);
        const allKpiReportsResponse = await Promise.allSettled(
          paths.map((path) => getKpiReports(payload, path, selectedDate)),
        );
        responses = allKpiReportsResponse.map((response) => response.value);

        const complaintRateResponse = await getReports(payload, selectedDate);

        const { reportCounts } = await collectionAndDeliveryDelay({
          storeId: activeStore.id,
        });
        setCollectionAndDeliveryDelayData(reportCounts);

        if (complaintRateResponse?.complaintRateDtos) {
          responses.push({
            complaintRateDtos: complaintRateResponse.complaintRateDtos,
          });
        }
      } finally {
        setLoadingCount((prev) => prev - 1);
      }

      const dtoArrays = responses.flatMap((response) =>
        Object.values(response),
      );
      const mergedPerformanceData = mergePerformanceDtos(
        [activeStore?.id],
        ...dtoArrays,
      );
      let filteredData = mergedPerformanceData.filter((item) => item !== null);
      setPerformanceData(filteredData);
      return responses;
    }
  }

  async function requestRegionalPaths(paths, selectedDate = 'daily') {
    if (paths.length) {
      setRegionalPerformanceData([]);
      let responses = [];
      const payload = { userId: `${user?.id}` };

      try {
        setLoadingCount((prev) => prev + 1);
        const pathsToProcess = isRegionalDirectore
          ? DASHBOARD_REPORT_PATHS.DIRECTORATE
          : paths.map((path) => `/directorate${path}`);
        const allKpiReportsResponse = await Promise.allSettled(
          pathsToProcess.map((path) =>
            getKpiReports(payload, path, selectedDate),
          ),
        );
        responses = allKpiReportsResponse
          .filter((response) => response.status === 'fulfilled')
          .map((response) => response.value);
      } finally {
        setLoadingCount((prev) => prev - 1);
      }

      if (!responses.length) {
        setRegionalPerformanceData([]);
        return responses;
      }

      const regionalData = responses.reduce((acc, curr) => {
        if (!acc) {
          return {
            ...curr,
            performancePercentageDtos: [
              ...(curr.performancePercentageDtos || []),
            ],
          };
        } else {
          if (
            acc.totalDeliveredOrders == null &&
            curr.totalDeliveredOrders != null
          ) {
            acc.totalDeliveredOrders = curr.totalDeliveredOrders;
          }
          acc.performancePercentageDtos.push(
            ...(curr.performancePercentageDtos || []),
          );
          return acc;
        }
      }, null);

      setRegionalPerformanceData(regionalData ? [regionalData] : []);
      return responses;
    }
  }

  async function fetchStoresByRegionId(regionId, setOptions, setSelected) {
    if (!regionId) {
      setOptions([]);
      setSelected(null);
      setSelectedStorePerformanceData(null);
      return;
    }
    try {
      setLoadingCount((prev) => prev + 1);
      const response = await getStoresByRegion(regionId);
      const stores = (response?.content || []).map((s) => ({
        value: s.id,
        label: `${s.code} - ${s.name}`,
      }));
      setOptions(stores);
      setSelected(null);
      setSelectedStorePerformanceData(null);
    } catch {
      setOptions([]);
    } finally {
      setLoadingCount((prev) => prev - 1);
    }
  }

  async function requestStorePerformance(storeId, currentDate = selectedDate) {
    const payload = { storeIds: [storeId] };
    let responses = [];
    try {
      setLoadingCount((prev) => prev + 1);
      const allKpiReportsResponse = await Promise.allSettled(
        DASHBOARD_REPORT_PATHS.STORE.map((path) =>
          getKpiReports(payload, path, currentDate),
        ),
      );
      responses = allKpiReportsResponse
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);
      const complaintRateResponse = await getReports(payload, currentDate);
      if (complaintRateResponse?.complaintRateDtos) {
        responses.push({
          complaintRateDtos: complaintRateResponse.complaintRateDtos,
        });
      }
    } finally {
      setLoadingCount((prev) => prev - 1);
    }
    const dtoArrays = responses.flatMap((response) => Object.values(response));
    const mergedData = mergePerformanceDtos([storeId], ...dtoArrays);
    const storeData = mergedData.find((item) => item !== null) || null;
    setSelectedStorePerformanceData(storeData);
    if (isRegionalDirectore || isRegionalCounty) {
      try {
        const { reportCounts } = await collectionAndDeliveryDelay({ storeId });
        setCollectionAndDeliveryDelayData(reportCounts);
      } catch {
        setCollectionAndDeliveryDelayData([]);
      }
    }
  }

  async function requestRegionForDirectorate(
    regionId,
    currentDate = selectedDate,
  ) {
    const payload = { userId: `${user?.id}` };
    let responses = [];
    try {
      setLoadingCount((prev) => prev + 1);
      const allKpiReportsResponse = await Promise.allSettled(
        DASHBOARD_REPORT_PATHS.REGION.map((path) =>
          getKpiReports(payload, path, currentDate),
        ),
      );
      responses = allKpiReportsResponse
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);
    } finally {
      setLoadingCount((prev) => prev - 1);
    }

    const mergedByRegionId = {};
    responses.forEach((responseItem) => {
      const responseArray = Array.isArray(responseItem)
        ? responseItem
        : responseItem?.regionId
          ? [responseItem]
          : [];
      responseArray.forEach((region) => {
        if (!mergedByRegionId[region.regionId]) {
          mergedByRegionId[region.regionId] = {
            ...region,
            performancePercentageDtos: [
              ...(region.performancePercentageDtos || []),
            ],
          };
        } else {
          mergedByRegionId[region.regionId].performancePercentageDtos.push(
            ...(region.performancePercentageDtos || []),
          );
        }
      });
    });

    const found = mergedByRegionId[regionId] || null;
    setDirectorateRegionPerformanceData(found);
  }

  async function requestRegionPaths(selectedDate = 'daily') {
    setRegionPerformanceData([]);
    const payload = { userId: `${user?.id}` };
    let responses = [];

    try {
      setLoadingCount((prev) => prev + 1);
      const allKpiReportsResponse = await Promise.allSettled(
        DASHBOARD_REPORT_PATHS.REGION.map((path) =>
          getKpiReports(payload, path, selectedDate),
        ),
      );
      responses = allKpiReportsResponse
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);
    } finally {
      setLoadingCount((prev) => prev - 1);
    }

    if (!responses.length) {
      setRegionPerformanceData([]);
      return;
    }

    const mergedByRegionId = {};
    responses.forEach((responseItem) => {
      const responseArray = Array.isArray(responseItem)
        ? responseItem
        : responseItem?.regionId
          ? [responseItem]
          : [];
      responseArray.forEach((region) => {
        if (!mergedByRegionId[region.regionId]) {
          mergedByRegionId[region.regionId] = {
            ...region,
            performancePercentageDtos: [
              ...(region.performancePercentageDtos || []),
            ],
          };
        } else {
          mergedByRegionId[region.regionId].performancePercentageDtos.push(
            ...(region.performancePercentageDtos || []),
          );
        }
      });
    });

    const mergedRegions = Object.values(mergedByRegionId);
    const newRegionOptions = mergedRegions.map((r) => ({
      value: r.regionId,
      label: `${r.regionCode} - ${r.regionName}`,
    }));
    setRegionOptions(newRegionOptions);
    setSelectedRegion((prev) => prev ?? mergedRegions[0]?.regionId ?? null);
    setRegionPerformanceData(mergedRegions);
  }

  function mergePerformanceDtos(selectedStores, ...dataArrays) {
    const mergedData = {};

    selectedStores.forEach((storeId) => {
      mergedData[storeId] = { storeId };
      dataArrays.forEach((dataArray) => {
        const storeData = dataArray.find((item) => item.storeId === storeId);
        if (storeData) {
          mergedData[storeId] = { ...mergedData[storeId], ...storeData };
        }
      });
    });

    return Object.values(mergedData).map((store) => {
      const mergedPerformanceDtos = [];
      dataArrays.forEach((dataArray) => {
        const storeData = dataArray.find(
          (item) => item.storeId === store.storeId,
        );
        if (storeData) {
          mergedPerformanceDtos.push(...storeData.performancePercentageDtos);
        }
      });

      if (mergedPerformanceDtos.length > 0) {
        return { ...store, performancePercentageDtos: mergedPerformanceDtos };
      } else {
        return null;
      }
    });
  }

  const getDate = () => {
    if (!performanceData[0]) {
      return '';
    }

    const startDate = moment(performanceData[0]?.startDate);

    if (selectedDate === 'daily') {
      return `${startDate.format('DD MMMM YYYY')}`;
    } else {
      return startDate.format('MMMM YYYY');
    }
  };

  const getTitle = () => {
    const generalPerformanceText = `${t('generalPerformance')} - ${t(
      selectedDate,
    )}`;

    if (
      !isRegionalDirectore &&
      !isRegionalCounty &&
      performanceData[0]?.totalDeliveredOrders
    ) {
      const orderText = `${performanceData[0]?.totalDeliveredOrders} ${t(
        'order',
      )}`;
      return (
        <>
          <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {generalPerformanceText}
          </span>
          <span
            style={{ color: '#828282', fontSize: '24px', fontWeight: 'bold' }}
          >
            {' '}
            | {orderText}
          </span>
        </>
      );
    } else {
      return (
        <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
          {generalPerformanceText}
        </span>
      );
    }
  };

  const gertAlertMessage = (message, type) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <Alert message={message} type={type} />
      </div>
    );
  };

  return (
    <>
      {loadingCount > 0 || reportTypeLoading ? (
        <div className="loadingDiv" style={{ top: 0, left: 0 }}>
          <Loading />
        </div>
      ) : null}
      <Card
        title={
          <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {getTitle()}
          </span>
        }
        bordered={false}
        className="dashboardCard"
        extra={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{ marginRight: 30, fontWeight: 'bold', fontSize: '16px' }}
            >
              {getDate()}
            </span>
            <Radio.Group
              name="radiogroup"
              defaultValue={selectedDate}
              value={selectedDate}
              optionType="button"
              disabled={!selectedStore.length && !isRegionalDirectore}
              buttonStyle="solid"
              onChange={async (e) => {
                const newDate = e.target.value;
                const requests = [];

                if (
                  selectedStore.length &&
                  !isRegionalDirectore &&
                  !isRegionalCounty
                ) {
                  requests.push(
                    requestAllPaths(selectedStore, allPaths, newDate),
                  );
                }

                if (isRegionalDirectore) {
                  requests.push(requestRegionalPaths(allPaths, newDate));
                  if (selectedDirectorateId) {
                    requests.push(
                      requestRegionForDirectorate(
                        selectedDirectorateId,
                        newDate,
                      ),
                    );
                    if (selectedDirectorateStore) {
                      requests.push(
                        requestStorePerformance(
                          selectedDirectorateStore,
                          newDate,
                        ),
                      );
                    }
                  }
                }

                if (isRegionalCounty) {
                  requests.push(requestRegionPaths(newDate));
                  if (selectedRegionStore) {
                    requests.push(
                      requestStorePerformance(selectedRegionStore, newDate),
                    );
                  }
                }

                await Promise.all(requests);
                setSelectedDate(newDate);
              }}
            >
              <Space>
                <Radio.Button
                  style={{ borderRadius: '10px' }}
                  key="daily"
                  value="daily"
                >
                  {t('daily')}
                </Radio.Button>
                <Radio.Button
                  style={{ borderRadius: '10px' }}
                  key="monthly"
                  value="monthly"
                >
                  {t('monthly')}
                </Radio.Button>
              </Space>
            </Radio.Group>
          </div>
        }
      >
        <div>
          {loadingCount > 0 ? (
            gertAlertMessage(t('dataLoading'), 'info')
          ) : (
            <>
              {isRegionalDirectore &&
                (regionalPerformanceData?.length > 0 ? (
                  regionalPerformanceData.map((data) => (
                    <>
                      <div
                        className="pt-5"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingLeft: '24px',
                          paddingRight: '24px',
                          marginBottom: '8px',
                        }}
                      >
                        <Typography.Title level={3} style={{ margin: 0 }}>
                          {t('directorate')}
                          {data.directorateName
                            ? ` - ${data.directorateName}`
                            : ''}
                          {' - '}
                          <span style={{ color: '#828282' }}>
                            {data.totalDeliveredOrders} {t('order')}
                          </span>
                        </Typography.Title>
                        <div
                          style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'flex-end',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#595959',
                                paddingLeft: '2px',
                              }}
                            >
                              {t('region')}
                            </span>
                            <Select
                              value={selectedDirectorateId}
                              onChange={(value) => {
                                setSelectedDirectorateId(value ?? null);
                                if (value) {
                                  requestRegionForDirectorate(value);
                                  fetchStoresByRegionId(
                                    value,
                                    setDirectorateStoreOptions,
                                    setSelectedDirectorateStore,
                                  );
                                } else {
                                  setDirectorateRegionPerformanceData(null);
                                  setDirectorateStoreOptions([]);
                                  setSelectedDirectorateStore(null);
                                  setSelectedStorePerformanceData(null);
                                }
                              }}
                              size="large"
                              style={{ minWidth: '200px', borderRadius: '8px' }}
                              placeholder={t('region')}
                              allowClear
                              options={directorateOptions}
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#595959',
                                paddingLeft: '2px',
                              }}
                            >
                              {t('store')}
                            </span>
                            <Select
                              value={selectedDirectorateStore}
                              onChange={(value) => {
                                setSelectedDirectorateStore(value ?? null);
                                if (value) {
                                  requestStorePerformance(value);
                                } else {
                                  setSelectedStorePerformanceData(null);
                                  setCollectionAndDeliveryDelayData([]);
                                }
                              }}
                              size="large"
                              style={{ minWidth: '200px', borderRadius: '8px' }}
                              placeholder={t('store')}
                              allowClear
                              disabled={!directorateStoreOptions.length}
                              options={directorateStoreOptions}
                            />
                          </div>
                        </div>
                      </div>
                      <RegionalPerformanceCard
                        key={data.directorateId}
                        metrics={metrics}
                        performanceData={data}
                        selectedPeriodDate={selectedDate}
                        selectedDirectorate={data.directorateId}
                        isRegionLevel={false}
                        setReportTypeLoading={setReportTypeLoading}
                      />
                      {selectedDirectorateId &&
                        directorateRegionPerformanceData && (
                          <>
                            <div
                              className="pt-5"
                              style={{
                                paddingLeft: '24px',
                                paddingRight: '24px',
                                marginBottom: '8px',
                              }}
                            >
                              <Typography.Title level={3} style={{ margin: 0 }}>
                                {t('region')}
                                {directorateRegionPerformanceData.regionName
                                  ? ` - ${directorateRegionPerformanceData.regionName}`
                                  : ''}
                                {' - '}
                                <span style={{ color: '#828282' }}>
                                  {
                                    directorateRegionPerformanceData.totalDeliveredOrders
                                  }{' '}
                                  {t('order')}
                                </span>
                              </Typography.Title>
                            </div>
                            <RegionalPerformanceCard
                              key={directorateRegionPerformanceData.regionId}
                              metrics={metrics}
                              performanceData={directorateRegionPerformanceData}
                              selectedPeriodDate={selectedDate}
                              selectedDirectorate={
                                directorateRegionPerformanceData.regionId
                              }
                              isRegionLevel={true}
                              setReportTypeLoading={setReportTypeLoading}
                            />
                          </>
                        )}
                      {selectedDirectorateStore &&
                        selectedStorePerformanceData && (
                          <>
                            <div
                              className="pt-5"
                              style={{
                                paddingLeft: '24px',
                                paddingRight: '24px',
                                marginBottom: '8px',
                              }}
                            >
                              <Typography.Title level={3} style={{ margin: 0 }}>
                                {t('store')}
                                {(() => {
                                  const opt = directorateStoreOptions.find(
                                    (o) => o.value === selectedDirectorateStore,
                                  );
                                  return opt ? ` - ${opt.label}` : '';
                                })()}
                                {' - '}
                                <span style={{ color: '#828282' }}>
                                  {
                                    selectedStorePerformanceData.totalDeliveredOrders
                                  }{' '}
                                  {t('order')}
                                </span>
                              </Typography.Title>
                            </div>
                            <PerformanceCard
                              key={selectedDirectorateStore}
                              metrics={metrics}
                              performanceData={selectedStorePerformanceData}
                              selectedPeriodDate={selectedDate}
                              selectedStore={[selectedDirectorateStore]}
                              setReportTypeLoading={setReportTypeLoading}
                            />
                          </>
                        )}
                    </>
                  ))
                ) : (
                  <>
                    <div className="pt-5" style={{ paddingLeft: '24px' }}>
                      <Typography.Title level={3} style={{ margin: 0 }}>
                        {t('directorate')} -{' '}
                      </Typography.Title>
                    </div>
                    {gertAlertMessage(t('noData'), 'info')}
                  </>
                ))}
              {isRegionalCounty &&
                (() => {
                  const selectedRegionData = regionPerformanceData.find(
                    (r) => r.regionId === selectedRegion,
                  );
                  return regionPerformanceData.length > 0 ? (
                    <>
                      <div
                        className="pt-5"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingLeft: '24px',
                          paddingRight: '24px',
                          marginBottom: '8px',
                        }}
                      >
                        <Typography.Title level={3} style={{ margin: 0 }}>
                          {t('region')}
                          {selectedRegionData?.regionName
                            ? ` - ${selectedRegionData.regionName}`
                            : ''}
                          {' - '}
                          <span style={{ color: '#828282' }}>
                            {selectedRegionData?.totalDeliveredOrders}{' '}
                            {t('order')}
                          </span>
                        </Typography.Title>
                        <div
                          style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'flex-end',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#595959',
                                paddingLeft: '2px',
                              }}
                            >
                              {t('region')}
                            </span>
                            <Select
                              value={selectedRegion}
                              onChange={(value) => setSelectedRegion(value)}
                              size="large"
                              style={{ minWidth: '200px', borderRadius: '8px' }}
                              options={regionOptions}
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#595959',
                                paddingLeft: '2px',
                              }}
                            >
                              {t('store')}
                            </span>
                            <Select
                              value={selectedRegionStore}
                              onChange={(value) => {
                                setSelectedRegionStore(value ?? null);
                                if (value) {
                                  requestStorePerformance(value);
                                } else {
                                  setSelectedStorePerformanceData(null);
                                  setCollectionAndDeliveryDelayData([]);
                                }
                              }}
                              size="large"
                              style={{ minWidth: '200px', borderRadius: '8px' }}
                              placeholder={t('store')}
                              allowClear
                              disabled={!regionStoreOptions.length}
                              options={regionStoreOptions}
                            />
                          </div>
                        </div>
                      </div>
                      {selectedRegionData ? (
                        <RegionalPerformanceCard
                          key={selectedRegionData.regionId}
                          metrics={metrics}
                          performanceData={selectedRegionData}
                          selectedPeriodDate={selectedDate}
                          selectedDirectorate={selectedRegionData.regionId}
                          isRegionLevel={true}
                          setReportTypeLoading={setReportTypeLoading}
                        />
                      ) : null}
                      {selectedRegionStore && selectedStorePerformanceData && (
                        <>
                          <div
                            className="pt-5"
                            style={{
                              paddingLeft: '24px',
                              paddingRight: '24px',
                              marginBottom: '8px',
                            }}
                          >
                            <Typography.Title level={3} style={{ margin: 0 }}>
                              {t('store')}
                              {(() => {
                                const opt = regionStoreOptions.find(
                                  (o) => o.value === selectedRegionStore,
                                );
                                return opt ? ` - ${opt.label}` : '';
                              })()}
                              {' - '}
                              <span style={{ color: '#828282' }}>
                                {
                                  selectedStorePerformanceData.totalDeliveredOrders
                                }{' '}
                                {t('order')}
                              </span>
                            </Typography.Title>
                          </div>
                          <PerformanceCard
                            key={selectedRegionStore}
                            metrics={metrics}
                            performanceData={selectedStorePerformanceData}
                            selectedPeriodDate={selectedDate}
                            selectedStore={[selectedRegionStore]}
                            setReportTypeLoading={setReportTypeLoading}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="pt-5" style={{ paddingLeft: '24px' }}>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                          {t('region')} -{' '}
                        </Typography.Title>
                      </div>
                      {gertAlertMessage(t('noData'), 'info')}
                    </>
                  );
                })()}
              {!(isRegionalDirectore || isRegionalCounty) &&
                (activeStore ? (
                  performanceData?.length > 0 ? (
                    performanceData.map((data) => (
                      <>
                        <PerformanceCard
                          key={data.storeId}
                          metrics={metrics}
                          performanceData={data}
                          selectedPeriodDate={selectedDate}
                          selectedStore={selectedStore}
                          setReportTypeLoading={setReportTypeLoading}
                        />
                      </>
                    ))
                  ) : (
                    <>
                      <Typography.Title
                        level={3}
                        className="pt-5 mt-5"
                        style={{ paddingLeft: '24px' }}
                      >
                        {t('store')} -{' '}
                      </Typography.Title>
                      {gertAlertMessage(t('noData'), 'info')}
                    </>
                  )
                ) : (
                  <>
                    <Typography.Title
                      level={3}
                      className="pt-5 mt-5"
                      style={{ paddingLeft: '24px' }}
                    >
                      {t('store')} -{' '}
                    </Typography.Title>
                    {gertAlertMessage(t('kpiWarningMessage'), 'warning')}
                  </>
                ))}
            </>
          )}
        </div>
      </Card>
      {loadingCount === 0 &&
        collectionAndDeliveryDelayData?.length > 0 &&
        (!(isRegionalDirectore || isRegionalCounty) ||
          selectedDirectorateStore ||
          selectedRegionStore) && (
          <Card>
            <StoreAndCourierOperationCard
              collectionAndDeliveryDelayData={collectionAndDeliveryDelayData}
            />
          </Card>
        )}
    </>
  );
};

export default Dashboard;
